import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  SimpleGrid,
  useToast,
  Box,
  Text,
  Code,
  useColorModeValue,
  Tooltip,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

// Helper component for labels with info tooltip
function LabelWithTooltip({ children, tooltip }) {
  return (
    <HStack spacing={1} display="inline-flex">
      <span>{children}</span>
      {tooltip && (
        <Tooltip label={tooltip} hasArrow placement="top" fontSize="xs">
          <span>
            <Icon as={FontAwesomeIcon} icon={faInfoCircle} boxSize={3} color="gray.400" cursor="help" />
          </span>
        </Tooltip>
      )}
    </HStack>
  );
}

const initialFormData = {
  name: '',
  destinationUrl: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
};

function LinkModal({ isOpen, onClose, onSubmit, link, isLoading }) {
  const [formData, setFormData] = useState(initialFormData);
  const [copied, setCopied] = useState(false);
  const toast = useToast();
  const isEditing = !!link;
  const previewBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (link) {
      setFormData({
        name: link.link_name || '',
        destinationUrl: link.destination_url || '',
        utmSource: link.utm_source || '',
        utmMedium: link.utm_medium || '',
        utmCampaign: link.utm_campaign || '',
        utmTerm: link.utm_term || '',
        utmContent: link.utm_content || '',
      });
    } else {
      setFormData(initialFormData);
    }
    setCopied(false);
  }, [link, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Link name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!formData.destinationUrl.trim()) {
      toast({
        title: 'Destination URL is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    await onSubmit(formData);
  };

  // Build preview URL with UTM params
  const buildPreviewUrl = () => {
    if (!formData.destinationUrl) return '';

    try {
      const url = new URL(formData.destinationUrl);

      if (formData.utmSource) url.searchParams.set('utm_source', formData.utmSource);
      if (formData.utmMedium) url.searchParams.set('utm_medium', formData.utmMedium);
      if (formData.utmCampaign) url.searchParams.set('utm_campaign', formData.utmCampaign);
      if (formData.utmTerm) url.searchParams.set('utm_term', formData.utmTerm);
      if (formData.utmContent) url.searchParams.set('utm_content', formData.utmContent);

      return url.toString();
    } catch {
      return formData.destinationUrl;
    }
  };

  const handleCopyUrl = () => {
    const shortUrl = link ? `${window.location.origin.replace(':3058', ':3059')}/l/${link.short_code}` : '';
    navigator.clipboard.writeText(shortUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{isEditing ? 'Edit Link' : 'Create New Link'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Short URL display for editing */}
              {isEditing && link?.short_code && (
                <Box p={4} bg={previewBg} borderRadius="xl">
                  <Text fontSize="sm" color="gray.500" mb={2}>Short URL</Text>
                  <HStack>
                    <Code flex={1} p={2} borderRadius="lg" fontSize="sm">
                      {window.location.origin.replace(':3058', ':3059')}/l/{link.short_code}
                    </Code>
                    <Button
                      size="sm"
                      colorScheme={copied ? 'green' : 'brand'}
                      onClick={handleCopyUrl}
                      leftIcon={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </HStack>
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>
                  <LabelWithTooltip tooltip="A name to identify this link (e.g., 'Twitter Bio Jan 2024')">
                    Link Name
                  </LabelWithTooltip>
                </FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Newsletter January CTA"
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  <LabelWithTooltip tooltip="The destination page users will land on">
                    Destination URL
                  </LabelWithTooltip>
                </FormLabel>
                <Input
                  name="destinationUrl"
                  value={formData.destinationUrl}
                  onChange={handleChange}
                  placeholder="https://yoursite.com/landing-page"
                  borderRadius="xl"
                />
              </FormControl>

              <Box p={4} bg={previewBg} borderRadius="xl">
                <Text fontSize="sm" fontWeight="500" mb={3}>UTM Parameters</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      <LabelWithTooltip tooltip="Identifies where traffic originates (e.g., twitter, newsletter)">
                        utm_source
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="utmSource"
                      value={formData.utmSource}
                      onChange={handleChange}
                      placeholder="e.g., twitter"
                      size="sm"
                      borderRadius="lg"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      <LabelWithTooltip tooltip="Marketing medium (e.g., social, email, cpc)">
                        utm_medium
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="utmMedium"
                      value={formData.utmMedium}
                      onChange={handleChange}
                      placeholder="e.g., social"
                      size="sm"
                      borderRadius="lg"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      <LabelWithTooltip tooltip="Campaign name or promotion identifier">
                        utm_campaign
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="utmCampaign"
                      value={formData.utmCampaign}
                      onChange={handleChange}
                      placeholder="e.g., jan_promo_2024"
                      size="sm"
                      borderRadius="lg"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      <LabelWithTooltip tooltip="Paid keywords (optional)">
                        utm_term
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="utmTerm"
                      value={formData.utmTerm}
                      onChange={handleChange}
                      placeholder="e.g., marketing+tools"
                      size="sm"
                      borderRadius="lg"
                    />
                  </FormControl>
                  <FormControl gridColumn={{ md: 'span 2' }}>
                    <FormLabel fontSize="sm">
                      <LabelWithTooltip tooltip="Differentiate between links (e.g., bio_link, post_cta)">
                        utm_content
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="utmContent"
                      value={formData.utmContent}
                      onChange={handleChange}
                      placeholder="e.g., bio_link"
                      size="sm"
                      borderRadius="lg"
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              {/* URL Preview */}
              {formData.destinationUrl && (
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={2}>Final URL Preview</Text>
                  <Code
                    p={3}
                    borderRadius="lg"
                    fontSize="xs"
                    display="block"
                    whiteSpace="pre-wrap"
                    wordBreak="break-all"
                  >
                    {buildPreviewUrl()}
                  </Code>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={isLoading}
              borderRadius="xl"
            >
              {isEditing ? 'Save Changes' : 'Create Link'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default LinkModal;
